import type { AttachmentUpload, RecordingKind } from '@live-stream/shared'
import { createCompositor, type CompositorSource, type RecordingProfile } from './compositor.js'
import { createMixer } from './mixer.js'
import { pickRecordingMime } from './parts.js'
import { PartUploader, type UploaderProgress } from './uploader.js'

/**
 * One recording, from the recorder's browser: the composite — the stage as
 * drawn, every voice mixed — and one audio file per participant, each a
 * MediaRecorder feeding a PartUploader. Participants come and go during a
 * meeting; the engine looks every couple of seconds and starts a track for
 * a voice it has not heard yet, finishing the file of one that has gone.
 * Nothing is kept in memory beyond the part being cut: the plan's rule.
 */

export interface AudioSource {
  peerId: string
  name: string
  track: MediaStreamTrack
}

export interface EngineDeps {
  recordingId: string
  roomSlug: string
  source: CompositorSource
  /** The frame to record in: its size, rate and bitrate. */
  profile: RecordingProfile
  /** Every voice right now: the recorder's own microphone and each remote participant's. */
  voices(): AudioSource[]
  /** A share's sound, when there is one. */
  screenAudio(): MediaStreamTrack | null
  begin(
    kind: RecordingKind,
    mimeType: string,
    participantPeerId?: string,
  ): Promise<{ fileId: string; partBytes: number }>
  address(fileId: string, partNumber: number): Promise<AttachmentUpload>
  complete(fileId: string, durationMs: number | null): Promise<void>
  onProgress(state: EngineProgress): void
}

export interface EngineProgress {
  /** Files still uploading, composite included. */
  filesPending: number
  partsPending: number
  bytesSent: number
  failed: string | null
}

interface Job {
  recorder: MediaRecorder
  uploader: PartUploader
  startedAt: number
  finished: Promise<void> | null
}

const TIMESLICE_MS = 2000
const SWEEP_MS = 2000

export class RecordingEngine {
  private composite: Job | null = null
  private readonly tracks = new Map<string, Job>() // by peerId
  private readonly progress = new Map<string, UploaderProgress>()
  private compositor: ReturnType<typeof createCompositor> | null = null
  private mixer = createMixer()
  private sweep: ReturnType<typeof setInterval> | null = null
  private stopping = false
  private failed: string | null = null

  constructor(private readonly deps: EngineDeps) {}

  /** Everything MediaRecorder needs is there. */
  static supported(): boolean {
    return (
      typeof MediaRecorder !== 'undefined' &&
      typeof HTMLCanvasElement !== 'undefined' &&
      'captureStream' in HTMLCanvasElement.prototype &&
      pickRecordingMime('video') !== null
    )
  }

  async start(): Promise<void> {
    const videoMime = pickRecordingMime('video')
    if (!videoMime) throw new Error('This browser cannot record video.')

    this.compositor = createCompositor(this.deps.source, this.deps.profile)
    const tracks: MediaStreamTrack[] = [...this.compositor.stream.getVideoTracks()]
    if (this.mixer) tracks.push(this.mixer.track)
    const stream = new MediaStream(tracks)

    const begun = await this.deps.begin('composite', videoMime)
    this.composite = this.startJob(begun.fileId, begun.partBytes, stream, videoMime, {
      videoBitsPerSecond: this.deps.profile.videoBitsPerSecond,
      audioBitsPerSecond: this.deps.profile.audioBitsPerSecond,
    })

    this.syncVoices()
    this.sweep = setInterval(() => void this.syncVoices(), SWEEP_MS)
  }

  private startJob(
    fileId: string,
    partBytes: number,
    stream: MediaStream,
    mimeType: string,
    options: MediaRecorderOptions,
  ): Job {
    const uploader = new PartUploader(
      {
        fileId,
        recordingId: this.deps.recordingId,
        roomSlug: this.deps.roomSlug,
        kind: stream.getVideoTracks().length > 0 ? 'composite' : 'per_track',
        mimeType,
        label: stream.getVideoTracks().length > 0 ? 'Meeting recording' : 'Audio track',
        startedAt: Date.now(),
        durationMs: null,
        finished: false,
      },
      partBytes,
      {
        addressFor: (n) => this.deps.address(fileId, n),
        complete: (ms) => this.deps.complete(fileId, ms),
        onProgress: (p) => {
          this.progress.set(fileId, p)
          if (p.failed && !this.failed) this.failed = p.failed
          this.report()
        },
      },
    )
    const recorder = new MediaRecorder(stream, { mimeType, ...options })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) uploader.push(e.data)
    }
    recorder.onerror = () => {
      this.failed ??= 'The browser stopped the recorder.'
      this.report()
    }
    recorder.start(TIMESLICE_MS)
    return { recorder, uploader, startedAt: Date.now(), finished: null }
  }

  /** Voices that appeared get a track file; voices that left have theirs finished. */
  private syncVoices(): void {
    if (this.stopping) return
    const voices = this.deps.voices()
    const audioMime = pickRecordingMime('audio')

    const present = new Set<string>()
    for (const voice of voices) {
      if (voice.track.readyState !== 'live') continue
      present.add(voice.peerId)
      this.mixer?.add(`voice:${voice.peerId}:${voice.track.id}`, voice.track)
      if (!this.tracks.has(voice.peerId) && audioMime) {
        const stream = new MediaStream([voice.track])
        void this.deps
          .begin('per_track', audioMime, voice.peerId)
          .then((begun) => {
            if (this.stopping) return
            const job = this.startJob(begun.fileId, begun.partBytes, stream, audioMime, {
              audioBitsPerSecond: 48_000,
            })
            this.tracks.set(voice.peerId, job)
            // A track that ends (a device switch, a departure) finishes its file;
            // the next sweep starts a new one if the voice is back.
            voice.track.addEventListener(
              'ended',
              () => {
                void this.finishJob(job)
                if (this.tracks.get(voice.peerId) === job) this.tracks.delete(voice.peerId)
              },
              { once: true },
            )
          })
          .catch((err) => {
            this.failed ??= err instanceof Error ? err.message : String(err)
            this.report()
          })
      }
    }
    for (const [peerId, job] of [...this.tracks]) {
      if (present.has(peerId)) continue
      this.tracks.delete(peerId)
      void this.finishJob(job)
    }

    const screen = this.deps.screenAudio()
    if (screen && screen.readyState === 'live') this.mixer?.add(`screen:${screen.id}`, screen, 0.8)
  }

  private finishJob(job: Job): Promise<void> {
    if (job.finished) return job.finished
    const durationMs = Date.now() - job.startedAt
    job.finished = new Promise<void>((resolve) => {
      const done = (): void => {
        job.uploader.stop(durationMs).then(resolve, () => resolve())
      }
      if (job.recorder.state === 'inactive') return done()
      job.recorder.addEventListener('stop', done, { once: true })
      try {
        job.recorder.stop()
      } catch {
        done()
      }
    })
    return job.finished
  }

  private report(): void {
    let partsPending = 0
    let bytesSent = 0
    let filesPending = 0
    for (const p of this.progress.values()) {
      partsPending += p.partsPending
      bytesSent += p.bytesSent
    }
    const jobs = [this.composite, ...this.tracks.values()].filter((j): j is Job => j !== null)
    for (const job of jobs)
      if (job.finished === null || job.recorder.state !== 'inactive') filesPending++
    this.deps.onProgress({ filesPending, partsPending, bytesSent, failed: this.failed })
  }

  /** Stop every recorder, upload what is left, complete every file. Resolves when storage has it all. */
  async stop(): Promise<void> {
    if (this.stopping) return
    this.stopping = true
    if (this.sweep) clearInterval(this.sweep)
    this.sweep = null
    const jobs = [this.composite, ...this.tracks.values()].filter((j): j is Job => j !== null)
    await Promise.all(jobs.map((job) => this.finishJob(job)))
    this.compositor?.stop()
    this.compositor = null
    this.mixer?.stop()
    this.mixer = null
    this.report()
    if (this.failed) throw new Error(this.failed)
  }

  /** How long the composite has been running. */
  get elapsedMs(): number {
    return this.composite ? Date.now() - this.composite.startedAt : 0
  }
}
